import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Plus,
  ChevronDown,
  ChevronUp,
  Package,
  MoreVertical, 
  Trash2, 
  Pencil, 
  Folder, 
  ArrowLeft, 
  BookOpen, 
  Sparkles,
  ClipboardList,
  ImageOff,
  Edit,
  Layers,
  Hammer
} from "lucide-react";
import Header from "../../components/Header";
import toast from "react-hot-toast";
import RoomEquipmentModal from "../components/RoomEquipmentModal";
import SchoolClassModal from "../components/SchoolClassModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import TextbookModal from "../components/TextbookModal";
import ClassroomInventoryModal from "../components/ClassroomInventoryModal";
import api from "../../connector/api";

// Reusable Premium Action-Panel Item Card
const ItemCard = ({ item, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const getConditionStyle = (cond) => {
    const check = String(cond).toUpperCase();
    if (check.includes("EXCELLENT") || check.includes("NEW")) return "bg-emerald-500 text-white border-emerald-600/20";
    if (check.includes("POOR") || check.includes("DAMAGED")) return "bg-rose-500 text-white border-rose-600/20";
    return "bg-amber-500 text-slate-950 border-amber-600/20";
  };

  return (
    <div className="relative group bg-white border font-dynamic-title border-slate-100 rounded-xl shadow-2xs hover:shadow-lg hover:border-slate-200/80 transition-all duration-300 flex flex-col overflow-hidden h-full">
      <div className="relative w-full aspect-video bg-slate-50 border-b border-slate-100 overflow-hidden shrink-0">
        {item.picture ? (
          <img src={item.picture} alt={item.name} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-slate-300 select-none bg-slate-50">
            <ImageOff size={24} className="stroke-[1.5]" />
            <span className="text-[9px] font-mono font-black uppercase tracking-widest text-slate-400">No Media Data</span>
          </div>
        )}

        {item.condition && (
          <div className="absolute top-3 left-3 z-10">
            <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md shadow-xs border ${getConditionStyle(item.condition)}`}>
              {item.condition}
            </span>
          </div>
        )}

        <div className="absolute top-2.5 right-2.5 z-20">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className={`p-1.5 rounded-xl transition-all shadow-2xs cursor-pointer backdrop-blur-md ${
              open ? "bg-[#002855] text-white" : "bg-white/80 text-slate-600 hover:text-slate-900 hover:bg-white"
            }`}
          >
            <MoreVertical size={14} className="stroke-[2.5px]" />
          </button>
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col justify-between space-y-4">
        <div className="space-y-1">
          <h4 className="font-bold text-slate-900 text-sm tracking-tight truncate group-hover:text-[#002855] transition-colors">{item.name}</h4>
          <p className="text-[11px] text-slate-400 font-medium leading-relaxed line-clamp-2">
            {item.description || "No supplemental descriptions documented for this specific equipment asset."}
          </p>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-slate-50 shrink-0">
          <div className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider">Log Node Matrix</div>
          <span className="text-[10px] font-mono font-bold bg-[#002855]/5 text-[#002855] border border-[#002855]/10 px-2 py-0.5 rounded-md">
            QTY: {item.quantity ?? 1} UNITS
          </span>
        </div>
      </div>

      {open && (
        <div ref={menuRef} className="absolute right-3 top-11 bg-white border border-slate-200/80 shadow-xl rounded-xl p-1 w-36 z-30 animate-in fade-in slide-in-from-top-2 duration-150">
          <button
            type="button"
            onClick={() => { onEdit(item); setOpen(false); }}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-[#002855] rounded-lg transition-all cursor-pointer"
          >
            <Pencil size={13} className="text-slate-400" />
            <span>Modify Asset</span>
          </button>
          <div className="h-px bg-slate-100 my-1" />
          <button
            type="button"
            onClick={() => { onDelete(item); setOpen(false); }}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
          >
            <Trash2 size={13} className="text-rose-400" />
            <span>Drop Record</span>
          </button>
        </div>
      )}
    </div>
  );
};

const SkeletonCard = () => (
  <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-4 animate-pulse shadow-2xs">
    <div className="flex items-center gap-3">
      <div className="w-14 h-14 bg-slate-100 rounded-xl shrink-0" />
      <div className="space-y-2 flex-1">
        <div className="h-3 bg-slate-200 rounded w-2/3" />
        <div className="h-2.5 bg-slate-100 rounded w-full" />
      </div>
    </div>
    <div className="flex gap-1.5"><div className="w-16 h-5 bg-slate-100 rounded-md" /><div className="w-12 h-5 bg-slate-100 rounded-md" /></div>
  </div>
);

export default function RoomInfo() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [room, setRoom] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedClassroom, setSelectedClassroom] = useState(null);

  const [schoolYears, setSchoolYears] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Modal States
  const [isClassModalOpen, setIsClassModalOpen] = useState(false);
  const [editingSchoolClass, setEditingSchoolClass] = useState(null);
  const [classFormData, setClassFormData] = useState({ name: "", employee: "", year: "" });
  const [formData, setFormData] = useState({ id: null, name: "", condition: "", quantity: 1, description: "", picture: null });
  
  const [isBookModalOpen, setIsBookModalOpen] = useState(false);
  const [editingTextbook, setEditingTextbook] = useState(null);
  const [bookFormData, setBookFormData] = useState({ 
    id: null,
    description: "",
    quantity_new: 0,
    quantity_good: 0,
    quantity_bad: 0,
    quantity_lost: 0,
    remarks: "" 
  });
  const [sectionOpen, setSectionOpen] = useState({
    structural: true,
    classes: true,
    textbooks: true,
    inventory: true,
  });

  const toggleSection = (section) => {
    setSectionOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [editingInventory, setEditingInventory] = useState(null);
  const [inventoryFormData, setInventoryFormData] = useState({
    id: null,
    item_type: "Chair",
    quantity_functional: 0,
    quantity_damaged: 0,
    remarks: ""
  });

  const resetForm = () => setFormData({ id: null, name: "", condition: "", quantity: 1, description: "", picture: null });
  const resetClassForm = () => { setEditingSchoolClass(null); setClassFormData({ name: "", employee: "", year: "" }); };

  // OPTIMIZATION: Convert employees array into a Hashmap to prevent inline CPU lag
  const employeeMap = useMemo(() => {
    return new Map(employees.map(emp => [emp.id, emp]));
  }, [employees]);

  // OPTIMIZATION: Fire all initialization calls concurrently via Promise.all
  useEffect(() => {
    const initializationPipeline = async () => {
      setLoading(true);
      try {
        const [roomRes, empRes, yearRes] = await Promise.all([
          api.get(`rooms/${id}/`),
          api.get("employees/"),
          api.get("years/")
        ]);

        setRoom(roomRes.data);
        setEquipment(roomRes.data.equipments || []);
        setEmployees(empRes.data?.results || empRes.data || []);
        setSchoolYears(yearRes.data?.results || yearRes.data || []);
      } catch (err) {
        toast.error("Error synchronizing local storage matrices.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    initializationPipeline();
  }, [id]);

  // Central refresh helper to sync states without clearing view settings
  const softRefreshRoomData = async (updatedClassroomId = null) => {
    try {
      const res = await api.get(`rooms/${id}/`);
      setRoom(res.data);
      setEquipment(res.data.equipments || []);
      
      if (updatedClassroomId || selectedClassroom) {
        const targetId = updatedClassroomId || selectedClassroom.id;
        const freshClass = res.data.schoolClasses?.find(c => c.id === targetId);
        if (freshClass) setSelectedClassroom(freshClass);
      }
    } catch (err) {
      console.error("Failed background data sync:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      payload.append("room", id);
      payload.append("name", formData.name);
      payload.append("condition", formData.condition);
      payload.append("quantity", Number(formData.quantity));
      payload.append("description", formData.description);
      if (formData.picture) payload.append("picture", formData.picture);

      if (formData.id) {
        await api.put(`equipment/${formData.id}/`, payload, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Equipment configuration updated!");
      } else {
        await api.post("equipment/", payload, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("New hardware asset mapped!");
      }

      await softRefreshRoomData();
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      toast.error("Failed to update structure assets");
    }
  };

  const handleClassSubmit = async (dataToSave) => {
    try {
      const payload = { name: dataToSave.name, employee: dataToSave.employee || null, year: dataToSave.year || null, room: id };
      
      let res;
      if (editingSchoolClass?.id) {
        res = await api.put(`school-classes/${editingSchoolClass.id}/`, payload);
        toast.success("School class configurations successfully updated!");
      } else {
        res = await api.post("school-classes/", payload);
        toast.success("New school class successfully provisioned!");
      }
      
      await softRefreshRoomData(res.data?.id);
      setIsClassModalOpen(false);
      resetClassForm();
    } catch (err) {
      toast.error("Failed to register class configuration parameters");
    }
  };

  const handleBookSubmit = async (dataToSave) => {
    try {
      const payload = {
        classroom: selectedClassroom.id,
        description: dataToSave.description,
        quantity_new: Number(dataToSave.quantity_new) || 0,
        quantity_good: Number(dataToSave.quantity_good) || 0,
        quantity_bad: Number(dataToSave.quantity_bad) || 0,
        quantity_lost: Number(dataToSave.quantity_lost) || 0,
        remarks: dataToSave.remarks || "",
      };

      if (editingTextbook?.id) {
        await api.put(`textbooks/${editingTextbook.id}/`, payload);
        toast.success("Textbook matrix updated!");
      } else {
        await api.post("textbooks/", payload);
        toast.success("New textbook matrix allocated!");
      }

      await softRefreshRoomData();
      setIsBookModalOpen(false);
      setEditingTextbook(null);
    } catch (err) {
      console.error(err);
      toast.error("Network schema mutation failure");
    }
  };

  const handleInventorySubmit = async (dataToSave) => {
    try {
      const payload = {
        classroom: selectedClassroom.id,
        item_type: dataToSave.item_type,
        quantity_functional: Number(dataToSave.quantity_functional) || 0,
        quantity_damaged: Number(dataToSave.quantity_damaged) || 0,
        remarks: dataToSave.remarks || "",
      };

      if (editingInventory?.id) {
        await api.put(`classroom-inventory/${editingInventory.id}/`, payload);
        toast.success("Inventory metrics updated successfully.");
      } else {
        await api.post("classroom-inventory/", payload);
        toast.success("Logged new asset inventory item!");
      }

      await softRefreshRoomData();
      setIsInventoryModalOpen(false);
      setEditingInventory(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to persist inventory data layer adjustments.");
    }
  };

  const handleEditBook = (book) => {
    setEditingTextbook(book);
    // 🌟 BUG FIX: Swapped from writing into equipment form data (`setFormData`) to book data (`setBookFormData`)
    setBookFormData({
      id: book.id,
      description: book.description,
      quantity_new: book.quantity_new ?? 0,
      quantity_good: book.quantity_good ?? 0,
      quantity_bad: book.quantity_bad ?? 0,
      quantity_lost: book.quantity_lost ?? 0,
      remarks: book.remarks || "",
    });
    setIsBookModalOpen(true);
  };

  const handleEditInventory = (inv) => {
    setEditingInventory(inv);
    setInventoryFormData({
      id: inv.id,
      item_type: inv.item_type,
      quantity_functional: inv.quantity_functional ?? 0,
      quantity_damaged: inv.quantity_damaged ?? 0,
      remarks: inv.remarks || ""
    });
    setIsInventoryModalOpen(true);
  };

  // Keep track of the specific data profile being scrubbed
  const [deleteTarget, setDeleteTarget] = useState({ id: null, type: null, name: "" });

  const handleDeleteEquipmentClick = (item) => {
    setDeleteTarget({ id: item.id, type: "equipment", name: item.name || "Hardware Asset" });
    setDeleteModalOpen(true);
  };

  const handleDeleteBookClick = (book) => {
    setDeleteTarget({ id: book.id, type: "textbook", name: book.description || "Textbook Asset Matrix" });
    setDeleteModalOpen(true);
  };

  const handleDeleteInventoryClick = (inv) => {
    setDeleteTarget({ id: inv.id, type: "inventory", name: `${inv.item_type} Component Row` });
    setDeleteModalOpen(true);
  };

  const handleSharedDelete = async () => {
    const { id, type } = deleteTarget;
    if (!id) return;

    try {
      if (type === "textbook") {
        await api.delete(`textbooks/${id}/`);
        toast.success("Textbook matrix removed from system nodes.");
      } else if (type === "equipment") {
        await api.delete(`equipment/${id}/`);
        toast.success("Hardware configuration purged!");
      } else if (type === "inventory") {
        await api.delete(`classroom-inventory/${id}/`);
        toast.success("Classroom inventory entry discarded.");
      }
      
      await softRefreshRoomData();
    } catch (err) {
      console.error(err);
      toast.error(`Failed to execute deletion on ${type} node`);
    } finally {
      setDeleteModalOpen(false);
      setDeleteTarget({ id: null, type: null, name: "" });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setActionDropdownOpen(false);
    };
    if (actionDropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [actionDropdownOpen]);

  return (
    <div className="flex flex-col min-h-screen font-dynamic-title bg-slate-50 text-slate-600 selection:bg-[#002855]/10">
      <header className="w-full bg-white border-b border-slate-200/80 sticky top-0 z-30">
        <Header />
      </header>
      
      <main className="flex-1 max-w-5xl w-full mx-auto p-4 sm:p-6 lg:p-8 space-y-5">
        
        {/* Action / Navigation Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <button
            onClick={() => navigate("/inventory/room-equipment")}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-[#002855] hover:border-slate-300 hover:shadow-xs transition-all cursor-pointer text-xs font-semibold w-fit"
          >
            <ArrowLeft size={14} className="transform group-hover:-translate-x-0.5 transition-transform" />
            Return to Room
          </button>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium uppercase tracking-wider text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl shadow-xs transition-all cursor-pointer active:scale-95"
            >
              Allocate Asset
              <ChevronDown size={14} className={`transform transition-transform duration-200 ${actionDropdownOpen ? "rotate-180" : ""}`} />
            </button>
            
            {actionDropdownOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-50 p-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
                <button onClick={() => { resetForm(); setIsModalOpen(true); setActionDropdownOpen(false); }} className="flex items-center w-full px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-[#002855] rounded-lg transition-all gap-2 cursor-pointer">
                  <Plus size={14} className="text-slate-400" />Add Structural Asset
                </button>
                <button onClick={() => { resetClassForm(); setIsClassModalOpen(true); setActionDropdownOpen(false); }} className="flex items-center w-full px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-[#002855] rounded-lg transition-all gap-2 cursor-pointer">
                  <Plus size={14} className="text-slate-400" />Create Classroom
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Hero Header Card */}
        <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-xs">
          <h1 className="text-sm font-bold text-slate-900 tracking-tight">
            {room?.code ? `Room ${room.code} Inspection` : "Loading Workspace Layout..."}
          </h1>
          <p className="text-xs text-slate-400 mt-1 font-normal max-w-2xl">
            Inspecting custom component registries, integrated facility configurations, and structural asset lifecycle endpoints.
          </p>
        </div>

        {/* SECTION 1: Allocated Structural Equipment */}
        <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
          <div 
            onClick={() => toggleSection('structural')}
            className="flex items-center justify-between p-4 bg-slate-50/70 border-b border-slate-200 cursor-pointer select-none group hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <ClipboardList size={16} className="text-[#002855]" />
              <h2 className="text-[10px] font-bold uppercase text-slate-800 tracking-wider">Structural Equipment</h2>
              <span className="bg-slate-200/75 text-slate-700 px-2 py-0.5 rounded-full font-mono text-[10px] font-bold">
                {equipment.length}
              </span>
            </div>
            {sectionOpen.structural ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </div>

          {sectionOpen.structural && (
            <div className="p-2 divide-y divide-slate-100">
              {loading ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 3 }).map((_, idx) => <div key={idx} className="h-12 bg-slate-100 rounded-xl animate-pulse" />)}
                </div>
              ) : equipment.length > 0 ? (
                equipment.map((item) => (
                  <div key={item.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 hover:bg-slate-50/80 rounded-xl transition-all group gap-3 border border-transparent hover:border-slate-100">
                    {/* Left Side: Image and Core Details */}
                    <div className="flex items-start gap-3.5 min-w-0 flex-1">
                      {/* Equipment Image Placeholder */}
                      <div className="w-14 h-14 rounded-xl bg-slate-100 border border-slate-200/60 overflow-hidden flex items-center justify-center flex-shrink-0 shadow-2xs">
                        {item.picture_url || item.picture ? (
                          <img src={item.picture_url || item.picture} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <Package size={20} className="text-slate-400" />
                        )}
                      </div>

                      {/* Metadata Block */}
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-slate-900 text-[11px] tracking-tight truncate">
                            {item.name || "Unnamed Structural Asset"}
                          </h4>
                          
                          {/* Quantity Badge */}
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-mono font-bold bg-slate-100 text-slate-700 border border-slate-200">
                            Qty: {item.quantity || 1}
                          </span>

                          {/* Dynamic Condition Status Badge */}
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold border ${
                            item.condition === 'New' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                            item.condition === 'Good' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                            item.condition === 'Fair' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                            'bg-rose-50 text-rose-700 border-rose-100' // Poor
                          }`}>
                            {item.condition || "Good"}
                          </span>
                        </div>

                        {/* Conditionally Render Description if it exists */}
                        {item.description && (
                          <p className="text-xs text-slate-500 line-clamp-1 max-w-xl font-normal" title={item.description}>
                            {item.description}
                          </p>
                        )}

                        {/* Unique Identifier */}
                        <p className="text-[10px] font-medium text-slate-400">
                          ID: <span className="font-mono text-slate-500">{item.serial_number || item.id}</span>
                        </p>
                      </div>
                    </div>

                    {/* Right Side: Action Control Group */}
                    <div className="flex items-center justify-end gap-1 flex-shrink-0 self-end sm:self-center border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-100">
                      <button 
                        type="button" 
                        onClick={() => { setFormData({ ...item, picture: null }); setIsModalOpen(true); }} 
                        className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                        title="Edit Asset"
                      >
                        <Edit size={14} />
                      </button>
                      <button 
                        type="button" 
                        onClick={() => handleDeleteEquipmentClick(item)} 
                        className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                        title="Delete Asset"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center text-center p-8 text-slate-400 font-medium gap-1.5">
                  <Sparkles size={18} className="text-slate-300" />
                  <p className="text-xs">No local room inventory items tracked.</p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* SECTION 2: Linked School Classes */}
        <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
          <div 
            onClick={() => toggleSection('classes')}
            className="flex items-center justify-between p-4 bg-slate-50/70 border-b border-slate-200 cursor-pointer select-none group hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <Folder size={16} className="text-[#002855]" />
              <h2 className="text-[10px] font-bold uppercase text-slate-800 tracking-wider">Linked School Classes</h2>
              <span className="bg-slate-200/75 text-slate-700 px-2 py-0.5 rounded-full font-mono text-[10px] font-bold">
                {room?.schoolClasses?.length || 0}
              </span>
            </div>
            {sectionOpen.classes ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </div>

          {sectionOpen.classes && !loading && (
            <div className="p-2 divide-y divide-slate-100">
              {room?.schoolClasses?.length > 0 ? (
                room.schoolClasses.map((cls) => {
                  const isSelected = selectedClassroom?.id === cls.id;
                  let employeeName = "No Faculty Member assigned";
                  if (cls.employee_detail) employeeName = cls.employee_detail.full_name;
                  else if (cls.employee) {
                    const matchedEmp = employeeMap.get(cls.employee);
                    if (matchedEmp) employeeName = matchedEmp.full_name || `${matchedEmp.first_name} ${matchedEmp.last_name}`.trim();
                  }

                  return (
                    <div
                      key={cls.id}
                      onClick={() => setSelectedClassroom(isSelected ? null : cls)}
                      className={`flex items-center justify-between p-3 rounded-xl transition-all group gap-4 cursor-pointer ${
                        isSelected ? "bg-blue-50/60 hover:bg-blue-50" : "hover:bg-slate-50/80"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${
                          isSelected ? "bg-white border-blue-200 text-[#002855]" : "bg-slate-50 border-slate-200/60 text-slate-400"
                        }`}>
                          <Folder size={16} />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-bold text-slate-900 text-sm truncate">{cls.name} <span className="text-xs text-slate-400 font-normal">({cls.year_detail?.name || "N/A"})</span></h4>
                          <p className="text-xs text-slate-400 truncate mt-0.5 font-medium">{employeeName}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button 
                          type="button" 
                          onClick={() => { setEditingSchoolClass(cls); setClassFormData({ name: cls.name, employee: cls.employee || "", year: cls.year?.id || cls.year || "" }); setIsClassModalOpen(true); }} 
                          className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                          <Pencil size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center p-6 text-slate-400 text-xs font-medium">No school classes associated with this section.</div>
              )}
            </div>
          )}
        </section>

        {/* Container Grid for Textbook Ledger and Container Inventory Lists */}
        {!loading && selectedClassroom && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-in fade-in duration-200">
            
            {/* TEXTBOOKS LIST PANEL */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs flex flex-col">
              <div 
                onClick={() => toggleSection('textbooks')}
                className="flex items-center justify-between p-4 bg-slate-50/70 border-b border-slate-200 cursor-pointer select-none group hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <BookOpen size={16} className="text-[#002855] flex-shrink-0" />
                  <h3 className="font-bold text-slate-800 text-[10px] uppercase tracking-wider truncate">
                    Textbooks ({selectedClassroom.textbooks?.length || 0})
                  </h3>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => { setBookFormData({ description: "", quantity_new: 0, quantity_good: 0, quantity_bad: 0, quantity_lost: 0, remarks: "" }); setEditingTextbook(null); setIsBookModalOpen(true); }}
                    className="p-1 rounded-md text-[#002855] hover:bg-[#002855]/10 transition-colors cursor-pointer"
                    title="Add Textbook"
                  >
                    <Plus size={16} />
                  </button>
                  {sectionOpen.textbooks ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                </div>
              </div>

              {sectionOpen.textbooks && (
                <div className="p-2 divide-y divide-slate-100 flex-1 overflow-y-auto max-h-[400px]">
                  {selectedClassroom.textbooks && selectedClassroom.textbooks.length > 0 ? (
                    selectedClassroom.textbooks.map((book) => {
                      const totalQty = (book.quantity_new || 0) + (book.quantity_good || 0) + (book.quantity_bad || 0) + (book.quantity_lost || 0);
                      return (
                        <div key={book.id} className="flex items-center justify-between p-2.5 hover:bg-slate-50/80 rounded-xl transition-all gap-3 group">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-12 bg-slate-100 rounded border border-slate-200/70 flex-shrink-0 overflow-hidden flex items-center justify-center">
                              {book.cover_url ? (
                                <img src={book.cover_url} alt={book.description} className="w-full h-full object-cover" />
                              ) : (
                                <BookOpen size={14} className="text-slate-400" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-bold text-slate-900 text-xs truncate" title={book.description}>{book.description}</h4>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {book.quantity_new > 0 && <span className="text-[9px] font-bold px-1 rounded bg-blue-50 text-blue-700 border border-blue-100">N:{book.quantity_new}</span>}
                                {book.quantity_good > 0 && <span className="text-[9px] font-bold px-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">G:{book.quantity_good}</span>}
                                {book.quantity_bad > 0 && <span className="text-[9px] font-bold px-1 rounded bg-amber-50 text-amber-700 border border-amber-100">B:{book.quantity_bad}</span>}
                                {book.quantity_lost > 0 && <span className="text-[9px] font-bold px-1 rounded bg-rose-50 text-rose-700 border border-rose-100">L:{book.quantity_lost}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="bg-slate-100 text-slate-800 font-mono text-[11px] font-bold px-1.5 py-0.5 rounded border border-slate-200">
                              {book.total_quantity !== undefined ? book.total_quantity : totalQty}
                            </span>
                            <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <button type="button" onClick={() => handleEditBook(book)} className="p-1 rounded text-slate-400 hover:text-blue-600 transition-colors cursor-pointer"><Edit size={13} /></button>
                              <button type="button" onClick={() => handleDeleteBookClick(book)} className="p-1 rounded text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"><Trash2 size={13} /></button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-400 text-xs font-normal p-4 text-center">No textbooks tracked inside this classroom container.</p>
                  )}
                </div>
              )}
            </div>

            {/* CLASSROOM HARDWARE INVENTORY LEDGER */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs flex flex-col">
              <div 
                onClick={() => toggleSection('inventory')}
                className="flex items-center justify-between p-4 bg-slate-50/70 border-b border-slate-200 cursor-pointer select-none group hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Layers size={16} className="text-[#002855] flex-shrink-0" />
                  <h3 className="font-bold text-slate-800 text-[10px] uppercase tracking-wider truncate">
                    Classroom Inventory ({selectedClassroom.inventory_items?.length || 0})
                  </h3>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => { setInventoryFormData({ id: null, item_type: "Chair", quantity_functional: 0, quantity_damaged: 0, remarks: "" }); setEditingInventory(null); setIsInventoryModalOpen(true); }}
                    className="p-1 rounded-md text-[#002855] hover:bg-[#002855]/10 transition-colors cursor-pointer"
                    title="Log Component"
                  >
                    <Plus size={16} />
                  </button>
                  {sectionOpen.inventory ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                </div>
              </div>

              {sectionOpen.inventory && (
                <div className="p-2 divide-y divide-slate-100 flex-1 overflow-y-auto max-h-[400px]">
                  {selectedClassroom.inventory_items && selectedClassroom.inventory_items.length > 0 ? (
                    selectedClassroom.inventory_items.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-2.5 hover:bg-slate-50/80 rounded-xl transition-all gap-3 group">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 bg-slate-50 rounded-xl border border-slate-200/60 flex flex-shrink-0 items-center justify-center text-[#002855]">
                            <Layers size={14} />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-bold text-slate-900 text-xs truncate">{inv.item_type}</h4>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[9px] font-bold px-1 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-100">Functional: {inv.quantity_functional}</span>
                              {inv.quantity_damaged > 0 && <span className="text-[9px] font-bold px-1 py-0.5 bg-amber-50 text-amber-700 rounded border border-amber-100">Damaged: {inv.quantity_damaged}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="bg-slate-100 text-slate-800 font-mono text-[11px] font-bold px-1.5 py-0.5 rounded border border-slate-200">
                            {inv.total_items || 0} U
                          </span>
                          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <button type="button" onClick={() => handleEditInventory(inv)} className="p-1 rounded text-slate-400 hover:text-blue-600 transition-colors cursor-pointer"><Edit size={13} /></button>
                            <button type="button" onClick={() => handleDeleteInventoryClick(inv)} className="p-1 rounded text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"><Trash2 size={13} /></button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-400 text-xs font-normal p-4 text-center">No asset models logged inside this classroom index.</p>
                  )}
                </div>
              )}
            </div>

          </div>
        )}
      </main>

      {/* Presentation Layer Modals */}
      <RoomEquipmentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} formData={formData} setFormData={setFormData} />
      <SchoolClassModal isOpen={isClassModalOpen} onClose={() => setIsClassModalOpen(false)} onSave={handleClassSubmit} schoolClass={editingSchoolClass} formData={classFormData} setFormData={setClassFormData} employees={employees} schoolYears={schoolYears} />
      <TextbookModal isOpen={isBookModalOpen} onClose={() => { setIsBookModalOpen(false); setEditingTextbook(null); }} onSave={handleBookSubmit} textbook={editingTextbook} formData={bookFormData} setFormData={setBookFormData} />
      <ClassroomInventoryModal isOpen={isInventoryModalOpen} onClose={() => { setIsInventoryModalOpen(false); setEditingInventory(null); }} onSave={handleInventorySubmit} inventoryItem={editingInventory} formData={inventoryFormData} setFormData={setInventoryFormData} />
      <DeleteConfirmationModal isOpen={deleteModalOpen} onClose={() => { setDeleteModalOpen(false); setDeleteTarget({ id: null, type: null, name: "" }); }} onConfirm={handleSharedDelete} itemName={deleteTarget.name} />
    </div>
  );
}